import { Component, Element, Host, Listen, Prop, Watch, h } from '@stencil/core';

import { onConditionTabSelected, onConditionTierConfirmed, onModalCancel, updateSelectedConditionTab, validateForm } from '../../../actions/actions.vehicle-conditions';
import EventBus from '../../../services/service.eventBus';
import { trackConditionSubNavClick, trackOpenModal } from '../../../tracking/tracking.conditions';
import { IcoVehicleConditionsLoader } from '../../loaders/IcoVehicleConditionsLoader';
import { IcoSimpleTabGroup } from '../../ui-common/tabs/IcoSimpleTabGroup';

const tabs = [
  {
    key: 'exterior',
    label: 'Exterior',
  },
  {
    key: 'interior',
    label: 'Interior',
  },
  {
    key: 'mechanical',
    label: 'Mechanical',
  },
];

@Component({
  tag: 'vehicle-conditions',
  styleUrl: 'vehicle-conditions.css',
  shadow: true,
})
export class VehicleConditions {
  @Element() el: HTMLElement;

  // Condition Tier Selection
  @Prop() conditionTierSelection: string;
  @Prop() isDescriptionOpen: boolean;
  @Prop() showConditionTierAlert: boolean;
  @Prop() showModal: boolean;

  @Prop() conditions = {};
  @Prop({ mutable: true }) activeSection: string = 'exterior';
  @Prop() activeExteriorSection: string = 'front';
  @Prop() activeInteriorSection: string = 'sch';
  @Prop() numberOfDoors: number = 2;
  @Prop() vehicleType: string = 'sedan';

  // Interior Questions
  @Prop() questionsCarpet = [];
  @Prop() questionsDashboard = [];
  @Prop() questionsDashboardLights = [];
  @Prop() questionsFrontSeats = [];
  @Prop() questionsHeadliner = [];
  @Prop() questionsMoldingTrim = [];
  @Prop() questionsOdors = [];
  @Prop() questionsRearSeats = [];
  @Prop() isThirdRowOptional = false;
  @Prop() numberOfRows = 1;
  @Prop() questionsCargoBed = [];

  // Exterior Questions
  // Front
  @Prop() questionsFrontBumper = [];
  @Prop() questionsFrontMoldingTrim = [];
  @Prop() questionsGrille = [];
  @Prop() questionsHood = [];

  // Driver
  @Prop() questionsDriverFrontFender = [];
  @Prop() questionsDriverFrontDoor = [];
  @Prop() questionsDriverMoldingTrim = [];
  @Prop() questionsDriverRearDoor = [];
  @Prop() questionsDriverRearPanel = [];
  @Prop() questionsDriverRockerPanel = [];

  // Passenger
  @Prop() questionsPassengerFrontFender = [];
  @Prop() questionsPassengerFrontDoor = [];
  @Prop() questionsPassengerMoldingTrim = [];
  @Prop() questionsPassengerRearDoor = [];
  @Prop() questionsPassengerRearPanel = [];
  @Prop() questionsPassengerRockerPanel = [];

  // Rear
  @Prop() questionsRearBumper = [];
  @Prop() questionsRoof = [];
  @Prop() questionsRearMoldingTrim = [];

  // Windows/Lights
  @Prop() questionsHeadlights = [];
  @Prop() questionsRearWindow = [];
  @Prop() questionsSideWindows = [];
  @Prop() questionsTaillights = [];
  @Prop() questionsWindshield = [];

  // Misc
  @Prop() questionsBrakes = [];
  @Prop() questionsTiresRims = [];
  @Prop() questionsTrunk = []; // a.k.a. liftgate (hatchback)
  @Prop() questionsMirrors = [];

  // badges
  @Prop() conditionPageFrontBadge = 0;
  @Prop() conditionPageDriverBadge = 0;
  @Prop() conditionPagePassengerBadge = 0;
  @Prop() conditionPageRearBadge = 0;
  @Prop() conditionPageWindowsLightsBadge = 0;

  @Prop() conditionPageSche = 0;
  @Prop() conditionPageDbl = 0;

  // Mechanical
  @Prop() questionsEngine = [];
  @Prop() questionsTransmission = [];
  @Prop() questionsSteering = [];
  @Prop() questionsExhaust = [];
  @Prop() questionsSuspension = [];
  @Prop() questionsHeatingAir = [];
  @Prop() questionsBatteryElectrical = [];
  @Prop() questionsStereo = [];
  @Prop() questionsOdometer = [];
  @Prop() questionsSunMoonRoof = [];
  @Prop() questionsAirbags = [];
  @Prop() questionsFrameStructural = [];

  @Prop() theme: string;

  @Watch('showModal')
  onShowModalChange(showModal: boolean) {
    if (showModal) {
      this.openConditionTierModal();
    }
  }

  attributesForSection(section: string) {
    const attributesPerSection = {
      exterior: {
        answers: this.conditions,
        defaultSection: this.activeExteriorSection,
        questionsFrontBumper: this.questionsFrontBumper,
        questionsFrontMoldingTrim: this.questionsFrontMoldingTrim,
        questionsGrille: this.questionsGrille,
        questionsHood: this.questionsHood,
        questionsDriverFrontFender: this.questionsDriverFrontFender,
        questionsDriverFrontDoor: this.questionsDriverFrontDoor,
        questionsDriverMoldingTrim: this.questionsDriverMoldingTrim,
        questionsDriverRearDoor: this.questionsDriverRearDoor,
        questionsDriverRearPanel: this.questionsDriverRearPanel,
        questionsDriverRockerPanel: this.questionsDriverRockerPanel,
        questionsPassengerFrontFender: this.questionsPassengerFrontFender,
        questionsPassengerFrontDoor: this.questionsPassengerFrontDoor,
        questionsPassengerMoldingTrim: this.questionsPassengerMoldingTrim,
        questionsPassengerRearDoor: this.questionsPassengerRearDoor,
        questionsPassengerRearPanel: this.questionsPassengerRearPanel,
        questionsPassengerRockerPanel: this.questionsPassengerRockerPanel,
        questionsRearBumper: this.questionsRearBumper,
        questionsRoof: this.questionsRoof,
        questionsRearMoldingTrim: this.questionsRearMoldingTrim,
        questionsHeadlights: this.questionsHeadlights,
        questionsRearWindow: this.questionsRearWindow,
        questionsSideWindows: this.questionsSideWindows,
        questionsTaillights: this.questionsTaillights,
        questionsWindshield: this.questionsWindshield,
        questionsBrakes: this.questionsBrakes,
        questionsTiresRims: this.questionsTiresRims,
        questionsTrunk: this.questionsTrunk,
        questionsMirrors: this.questionsMirrors,
        numberOfDoors: this.numberOfDoors,
        vehicleType: this.vehicleType,
        conditionPageFrontBadge: this.conditionPageFrontBadge,
        conditionPageDriverBadge: this.conditionPageDriverBadge,
        conditionPagePassengerBadge: this.conditionPagePassengerBadge,
        conditionPageRearBadge: this.conditionPageRearBadge,
        conditionPageWindowsLightsBadge: this.conditionPageWindowsLightsBadge,
      },
      interior: {
        answers: this.conditions,
        defaultSection: this.activeInteriorSection,
        isThirdRowOptional: this.isThirdRowOptional,
        questionsDashboard: this.questionsDashboard,
        questionsDashboardLights: this.questionsDashboardLights,
        questionsFrontSeats: this.questionsFrontSeats,
        questionsRearSeats: this.questionsRearSeats,
        questionsCargoBed: this.questionsCargoBed,
        questionsHeadliner: this.questionsHeadliner,
        questionsCarpet: this.questionsCarpet,
        questionsMoldingTrim: this.questionsMoldingTrim,
        questionsOdors: this.questionsOdors,
        numberOfRows: this.numberOfRows,
        vehicleType: this.vehicleType,
        conditionPageSche: this.conditionPageSche,
        conditionPageDbl: this.conditionPageDbl,
      },
      mechanical: {
        answers: this.conditions,
        questionsEngine: this.questionsEngine,
        questionsTransmission: this.questionsTransmission,
        questionsSteering: this.questionsSteering,
        questionsExhaust: this.questionsExhaust,
        questionsSuspension: this.questionsSuspension,
        questionsHeatingAir: this.questionsHeatingAir,
        questionsBatteryElectrical: this.questionsBatteryElectrical,
        questionsStereo: this.questionsStereo,
        questionsOdometer: this.questionsOdometer,
        questionsSunMoonRoof: this.questionsSunMoonRoof,
        questionsAirbags: this.questionsAirbags,
        questionsFrameStructural: this.questionsFrameStructural,
      },
    };

    const allowedSections = Object.keys(attributesPerSection);
    const keys = allowedSections;

    return keys.includes(section) ? attributesPerSection[section] : {};
  }

  connectedCallback() {
    updateSelectedConditionTab(this.activeSection);
  }

  componentWillUpdate() {
    validateForm();
  }

  componentDidLoad() {
    EventBus.publish('ComponentHasLoaded', 'vehicle-conditions');
  }

  onCancelClick() {
    this.closeConditionTierModal();
    onModalCancel();
  }

  closeConditionTierModal() {
    const modal = this.el.shadowRoot.querySelector('ico-modal#condition-picker-modal') as HTMLIcoModalElement;
    modal?.close();
  }

  openConditionTierModal() {
    const modal = this.el.shadowRoot.querySelector('ico-modal#condition-picker-modal') as HTMLIcoModalElement;
    modal?.open();
    trackOpenModal();
  }

  onSelectedTab(key: string) {
    onConditionTabSelected(key);
    this.setActiveSection(key);
  }

  setActiveSection(name: string) {
    this.activeSection = name;
  }

  actions = {
    select: this.onSelectedTab.bind(this),
  };

  @Listen('conditionSubNavClick', { capture: true })
  handleConditionSubNavClick(ev: CustomEvent) {
    trackConditionSubNavClick(ev.detail);
  }

  render() {
    const modalStyle = {
      'mdLrg:w-condition-modal-md lg:w-condition-modal-lg': true,
      'h-cond-tier-desc': this.isDescriptionOpen,
    };

    const footerStyle = {
      'flex justify-between': true,
      hidden: this.isDescriptionOpen,
    };

    return (
      <Host>
        <div class={`${this.theme}-theme box-shadow`}>
          <IcoSimpleTabGroup actions={this.actions} selectedKey={this.activeSection} tabs={tabs} dataTestId={`vc`}></IcoSimpleTabGroup>
          <IcoVehicleConditionsLoader {...this.attributesForSection(this.activeSection)} section={this.activeSection} />
          <ico-modal id="condition-picker-modal" shouldCloseOnBackdrop={false} modalSize="large" hideOverflow={!this.isDescriptionOpen} dataTestId={'vc-cts-modal'}>
            <div class={modalStyle} slot="main">
              <condition-picker placement="modal" show-alert={true} />
              {this.isDescriptionOpen && <div class="pb-6"></div>}
            </div>
            <div class={footerStyle} slot="footer">
              <ico-button
                data-testId="vc-cts-btn-cancel"
                label="Cancel"
                buttonSize="large"
                buttonStyle="tertiary"
                whenClicked={() => {
                  this.onCancelClick();
                }}
              />
              <ico-button
                data-testId="vc-cts-btn-next"
                label="Next"
                buttonSize="large"
                buttonStyle="primary"
                whenClicked={onConditionTierConfirmed}
                disabled={!this.conditionTierSelection}
              />
            </div>
          </ico-modal>
        </div>
      </Host>
    );
  }
}
